import { useState } from 'react';
import { css } from '@emotion/react';
import {
  AnalyticsPageSubtitleWrapper,
  AnalyticsPageViewTabs,
  DemoDataBanner,
  GeneralFeedbackButton,
  LargeModeOnlyFeature,
  MultiLocationLargeModeOnlyFeature,
  useAnalyticsOrgLocations,
} from '@frontend/analytics-shared';
import { Header } from '@frontend/header-navigation';
import { TrueLarkMissedCallPromoBanner } from '@frontend/high-value-adoption';
import { useTranslation } from '@frontend/i18n';
import { Page } from '@frontend/page';
import { breakpoints } from '@frontend/responsiveness';
import { useIsSmallMode } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Tabs } from '@frontend/design-system';
import { CommunicationAnalyticsReleaseBanner } from '../components/communication-analytics-release-banner';
import { PhoneAnalyticsFilters } from '../components/filters/phone-analytics-filters';
import { PhoneReportCharts } from '../components/phone-report-charts';
import { PhoneReportTable } from '../components/phone-report-table';
import { DEFAULT_ACTIVE_TAB_VIEW, URLs } from '../constants';
import { useOnPhoneAnalyticsPageLoad } from '../hooks/use-on-phone-analytics-page-load';
import { trackingIds } from '../tracking-ids';

export const PhoneAnalytics = () => {
  const { t } = useTranslation('analytics-phone');
  const [activeTab, setActiveTab] = useState<string>(DEFAULT_ACTIVE_TAB_VIEW);
  const { isSmallMode } = useIsSmallMode();
  const { filters, isDemoAccount, pageSubtitle } = useOnPhoneAnalyticsPageLoad();
  const { locationIds } = useAnalyticsOrgLocations({
    isAllAccessScopeEnabled: true,
    isDemoAccount,
    module: 'PHONE',
  });
  const selectedLocationIds = filters.location_id;
  return (
    <>
      {isDemoAccount && <DemoDataBanner title={t("You're taking Phone Analytics for a test drive with demo data!")} />}
      <CommunicationAnalyticsReleaseBanner />
      <Tabs initialTab={DEFAULT_ACTIVE_TAB_VIEW} onChange={setActiveTab}>
        <Page>
          <Page.Header>
            <Page.Header.Heading css={styles.pageHeading}>
              <Page.Header.Title className='header-title'>
                <Header title={t('Phone Analytics')} />
                <GeneralFeedbackButton selectedLocationFromPageFilter={selectedLocationIds} />
              </Page.Header.Title>
              <AnalyticsPageSubtitleWrapper>
                <Page.Header.Subtitle
                  subtitle={t('Showing results for {{filtersInfo}}', {
                    filtersInfo: pageSubtitle,
                  })}
                />
                <AnalyticsPageViewTabs trackingIds={trackingIds} videoId={URLs.PHONE_ANALYTICS_VIDEO_ID} />
              </AnalyticsPageSubtitleWrapper>
            </Page.Header.Heading>
          </Page.Header>

          <Page.Body>
            <PhoneAnalyticsFilters activeTab={activeTab} />
            {!isDemoAccount && (
              <TrueLarkMissedCallPromoBanner
                locationIds={locationIds}
                source='phone-analytics'
                containerStyles={styles.promoBanner}
              />
            )}
            <Tabs.Panel controller='table-view'>
              {isSmallMode ? <LargeModeOnlyFeature featureName={t('Table View')} /> : <PhoneReportTable />}
            </Tabs.Panel>
            <Tabs.Panel controller='charts-view'>{activeTab === 'charts-view' && <PhoneReportCharts />}</Tabs.Panel>
          </Page.Body>
        </Page>
      </Tabs>
      <MultiLocationLargeModeOnlyFeature locationCount={filters.location_id?.length} />
    </>
  );
};

const styles = {
  pageHeading: css`
    .header-title {
      width: 100%;
      display: flex;
      justify-content: space-between;
      @container (max-width:  ${breakpoints.xsmall.max}px) {
        flex-direction: row;
      }
    }
    .page-action {
      @container (max-width:  ${breakpoints.xsmall.max}px) {
        width: 100%;
      }
    }
  `,
  promoBanner: css`
    margin: ${theme.spacing(3, 0)};
  `,
};
